import React, { useState, useEffect } from 'react';

import { useOutletContext, useNavigate } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import { createColumnHelper } from "@tanstack/react-table";

import { Grid, Box, Typography, } from "@mui/material";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import ModalContainer from 'components/ModalContainer';
import { SolidButton } from "components/buttons/SolidButton";

import ModalforCheck from '../modalCheck.js';
import ModalForAddOrgOrSite from '../ModalForAddOrgOrSite.js';

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Organization = () => {
  const {scopeData, setScopeData, scopeReady} = useOutletContext();
  
  const { bread, setBread } = useBreadStorageContext();

  const [manageOrgsUuid, _setManageOrgsUuid] = useState(uuidv4());
  const [nowUuid, setNowUuid] = useState('');

  const { dataMap, sendRequest } = useDataSourceManager();

  const [openModalForAddOrg, setOpenModalForAddOrg] = useState(false);
  const [modalForAddOrgTitle, setModalForAddOrgTitle] = useState('');
  const [modalForAddOrgCancelBtn, setModalForAddOrgCancelBtn] = useState('');
  const [modalForAddOrgSaveBtn, setModalForAddOrgSaveBtn] = useState('');
  
  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [modalCheckTitle, setModalCheckTitle] = useState('');
  const [modalCheckContent, setModalCheckContent] = useState('');
  const [modalCheckCancelBtn, setModalCheckCancelBtn] = useState('');
  const [modalCheckSaveBtn, setModalCheckSaveBtn] = useState('');
  
  const [modalFrom, setModalFrom] = useState('');
  const [modalAction, setModalAction] = useState(false);
  const [updateItem, setUpdateItem] = useState({});
  const [enableAction, setEnableAction] = useState(false);

  const getDatas = async () => {
    setNowUuid(manageOrgsUuid)

    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    let tmpResource = '/ents/:entId/orgs.read';
    let tmpResourceAPIval = {entId: tmpSelectedBread.realm.id }
    sendRequest( tmpResource, manageOrgsUuid, tmpResourceAPIval);
  };

  const mergeData= (orgList) => {
    let resultAry = []
    
    let manageOrgList = []
    if(orgList.data && orgList.data.orgs.length > 0){
      manageOrgList = orgList.data.orgs
    }

    for(let childEnt of manageOrgList){
      let child = {
        id: childEnt.orgId,
        name: childEnt.orgName,
        roleId: childEnt.orgRoleId,
        roleName: childEnt.orgRoleName,
      }
      resultAry.push(child)
    }
    
    return resultAry;
  }

  const handleAddItem = () =>{
    if(!enableAction) return;
    setModalFrom('Add')
    let newItem = {
      id:'',name: '', hasManageOrg: false,
      roleId: '',roleName: '',
    }
    setUpdateItem(newItem)
    
    setModalForAddOrgTitle('Add Organization')
    setModalForAddOrgCancelBtn('Cancel')
    setModalForAddOrgSaveBtn('Save')
    setOpenModalForAddOrg(true)
  }
  
  const handleScopeLink = (item) =>{
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');

    // save storage
    tmpSelectedBread.orgs = item;
    tmpSelectedBread.site = {};
    localStorage.setItem('selectBreadcrumbs', JSON.stringify(tmpSelectedBread));

    setBread(tmpSelectedBread)
    // navigate
    navigatePage(item);
  }

  const navigate = useNavigate();
  const navigatePage = () => {
    let path = "/org/sites";
    navigate(path);
  }

  const checkPermissions = () => {
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    if(tmpSelectedBread && Object.keys(tmpSelectedBread).length > 0){
      if(tmpSelectedBread.realm.roleId === 1 || tmpSelectedBread.realm.roleId === 2){
        setEnableAction(true)
      }else{
        setEnableAction(false)
      }
    }
  }

  useEffect(() => {
    checkPermissions()
  }, [bread]);

  useEffect(() => {
    if(scopeReady){
      checkPermissions()
    }
  }, [scopeReady]);

  function handleActionDenied(message) {
    setOpenModalForAddOrg(false);
    setModalCheckTitle('Action denied')
    setModalCheckContent(message)
    setModalCheckCancelBtn('')
    setModalCheckSaveBtn('OK')
    setModalFrom('error')
    setOpenModalCheck(true)
  }

  useEffect(() => {
    if(nowUuid !== ''){
      switch (nowUuid) {
        case manageOrgsUuid:
          if(dataMap[manageOrgsUuid]?.code !== undefined){
            setModalCheckTitle('Action denied')
            setModalCheckContent(dataMap[manageOrgsUuid].message)
            setModalCheckCancelBtn('')
            setModalCheckSaveBtn('OK')
            setModalFrom('error')
            setOpenModalCheck(true)
          }else if(dataMap[manageOrgsUuid] !== undefined){
            const result = mergeData(dataMap[manageOrgsUuid])
            setScopeData(prev => ({
              ...prev,
              orgs: result
            }));
          }

          setNowUuid('')
          break;
        default:
          break;
      }
    }
  }, [dataMap]);

  useEffect(() => {
    if(modalAction === true){
      if(modalFrom === 'Add'){
        if(checkMockup === 'true'){
          let addItem = cloneDeep(updateItem)
          addItem.id = uuidv4();

          setScopeData(prev => ({
            ...prev,
            orgs: [...prev.orgs, addItem]
          }));
        }
      }
      setModalAction(false)
    }
  }, [modalAction]);

  const columnHelper = createColumnHelper();
  const columnsData = [
    columnHelper.accessor("name", {
      header: "Name",
      cell: (info) => (
        <div onClick={() => handleScopeLink(info.row.original)} style={{color:'#707ca8', cursor:'pointer', }}>
          <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textDecoration:'underline',}}>
            {info.getValue()}
            </span>
        </div>
      ),
    }),
    columnHelper.accessor("roleName", {
      header: "Role",
    }),
  ];

  const table = useTableInstance({
    tableName: "hierarchyRealmOrg",
    data: scopeData.orgs,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmOrg",
    data: '',
    columns: columnsData,
    enableSorting: true,
  });

  return (
      <>
        <div className={`main-card-content`}
          style={{flex: (scopeData.orgs.length>0)?'':'1', 
          display:'flex', flexDirection:'column'}}>
          <div>
            <Grid container style={{justifyContent:'space-between', marginBottom:'10px'}}>
              <Box style={{display:'flex'}}>
                <Grid container sx={{alignItems: "center",}}>
                </Grid>
              </Box>
              <Box style={{display:'flex'}}>
                { scopeData.orgs.length>0 && <Grid container sx={{alignItems: "center", gap:'15px'}}>
                  <SearchInput
                    value={table.globalFilter}
                    onChange={table.setGlobalFilter}
                  />
                  {enableAction && 
                    <SolidButton 
                      disabled={enableAction? false: true}
                      startIcon={<AddIcon />}
                      height="32px"
                      onClick={() => handleAddItem()}
                    >
                      Add Organization
                    </SolidButton>
                  }
                </Grid> }
              </Box>
            </Grid>
          </div>
          {scopeData.orgs.length > 0 && 
            <TableField
              {...table}
            />
          }
          { scopeData.orgs.length === 0 && <>
            <TableField
              {...emptyHead}
            />    
            <div style={{flex:'1',alignContent:'center', justifyItems:'center'}}>
              <Grid container style={{justifyContent:'center', marginBottom:'40px'}}>
                <Typography sx={{textAlign:'center'}}
                fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                  No entry in the table, you can create a new organization.
                </Typography>
              </Grid>
              <Box>
                {enableAction && 
                  <SolidButton 
                    disabled={enableAction? false: true}
                    startIcon={<AddIcon />}
                    height="32px"
                    onClick={() => handleAddItem()}
                  >
                    Add Organization
                  </SolidButton>
                }
              </Box>
            </div> 
          </>}
        </div>
        <ModalContainer
          display={openModalCheck}
          modalWidth={'500px'} //550px, 750px, empty=1000px,
          children={
            <ModalforCheck
              title={modalCheckTitle}
              content={modalCheckContent}
              cancelBtn={modalCheckCancelBtn}
              saveBtn={modalCheckSaveBtn}
              setOpenModal={setOpenModalCheck}
              setModalAction={setModalAction}
            />
          }
        />
        <ModalForAddOrgOrSite
          auth="Organization"
          display={openModalForAddOrg}
          title={modalForAddOrgTitle}
          setOpenModal={setOpenModalForAddOrg}
          cancelBtn={modalForAddOrgCancelBtn}
          saveBtn={modalForAddOrgSaveBtn}
          errorHandling={handleActionDenied}
          refresh={getDatas}
        />
      </>
  );
};

export default Organization;